import { useState } from "react";
import { toast } from "sonner";
import { CalendarClock, RefreshCw, Mail, Loader2 } from "lucide-react";
import { http, errMsg } from "../../../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../ui/alert-dialog";

const day = (d) => new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
const STATE = { up_to_date: ["Up to date", "bg-emerald-50 text-emerald-800 border-emerald-300"], due_soon: ["Due soon", "bg-amber-50 text-amber-800 border-amber-300"], overdue: ["Update due", "bg-red-50 text-red-700 border-red-300"] };

const Field = ({ label, value, id }) => (
  <div><div className="text-xs text-stone-500">{label}</div><div data-testid={id} className="mt-1 text-[15px] text-[#2B2A26]">{value}</div></div>
);

const UpdateNow = ({ kit, c, reload, busy }) => {
  const [open, setOpen] = useState(false);
  const go = async () => {
    try { await http.post(`/kits/${kit.id}/update-guide`); toast.success("Updating the guide — every source is being re-researched"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <button data-testid="update-guide-button" disabled={busy} onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm text-white hover:opacity-90 transition-opacity disabled:opacity-40" style={{ background: c.dark }}>
        <RefreshCw size={15} /> Update guide now
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="update-guide-dialog" className="bg-white text-[#2B2A26]">
          <AlertDialogHeader>
            <AlertDialogTitle>Start Edition {(kit.accuracy?.number || 1) + 1}?</AlertDialogTitle>
            <AlertDialogDescription>Re-researches every venue, rewrites the chapters and re-typesets the PDF and app. Uses AI credit (up to the $8 kit limit). Your store listing and publish status are kept.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="update-guide-cancel" className="bg-white">Not now</AlertDialogCancel>
            <AlertDialogAction data-testid="update-guide-confirm" onClick={go} className="bg-[#2B2A26] text-white">Update guide</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const LinkBtn = ({ id, label, path, done }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const { data } = await http.post(path); toast.success(done(data)); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return <button data-testid={id} onClick={go} disabled={busy} className="text-xs text-[#2B2A26] hover:underline disabled:opacity-40 inline-flex items-center gap-1">{busy && <Loader2 size={11} className="animate-spin" />}{label}</button>;
};

const History = ({ acc, c }) => (
  <div data-testid="edition-history" className="p-6">
    <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-stone-400 mb-4">Edition history</div>
    {[...acc.history].reverse().map((h) => (
      <div key={h.n} data-testid={`edition-history-${h.n}`} className="border-l-2 pl-4 mb-3" style={{ borderColor: c.primary }}>
        <span className="font-display text-lg text-[#2B2A26]">Edition {h.n}</span> <span className="text-xs text-stone-500">{day(h.date)}</span>
        {h.note && <div className="text-xs text-stone-500 mt-0.5">{h.note}</div>}
      </div>
    ))}
  </div>
);

export const AccuracyPanel = ({ kit, c, reload, busy }) => {
  const acc = kit.accuracy;
  if (!acc || kit.bespoke) return null;
  const [lbl, cls] = STATE[acc.state];
  return (
    <section data-testid="accuracy-panel" className="rise grid lg:grid-cols-2 border border-stone-200 bg-white mb-8">
      <div className="p-6 border-b lg:border-b-0 lg:border-r border-stone-200" style={{ background: c.tint }}>
        <div className="flex items-center justify-between gap-3">
          <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-stone-400 flex items-center gap-2"><CalendarClock size={14} /> Accuracy · 6-month update cycle</div>
          <span data-testid="accuracy-state" className={`font-mono-ui text-[10px] tracking-widest uppercase border px-2 py-1 ${cls}`}>{lbl}</span>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-5">
          <Field label="Edition" value={<span className="font-display text-xl">{acc.number}</span>} id="accuracy-edition" />
          <Field label="Last updated" value={day(acc.last_updated)} id="accuracy-last-updated" />
          <Field label="Next update due" value={day(acc.next_due)} id="accuracy-next-due" />
        </div>
        <div className="mt-6"><UpdateNow kit={kit} c={c} reload={reload} busy={busy} /></div>
        <p className="text-xs text-stone-500 mt-3 leading-relaxed">Re-researches every source, rewrites all chapters, re-typesets the PDF and app and logs the new edition. Your store listing, price and publish status are kept.</p>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-5 pt-4 border-t border-stone-200">
          <span className="text-xs text-stone-500 flex items-center gap-2"><Mail size={13} /> Email reminders: {acc.reminders}</span>
          <span className="flex gap-4">
            <LinkBtn id="send-test-reminder" label="Send test reminder" path={`/kits/${kit.id}/reminder-test`} done={(d) => `Test reminder sent to ${d.to}`} />
            <LinkBtn id="email-past-buyers" label={`Email past buyers${acc.buyers ? ` (${acc.buyers})` : ""}`} path={`/kits/${kit.id}/email-buyers`} done={(d) => `Update email sent to ${d.sent} buyer(s)`} />
          </span>
        </div>
      </div>
      <History acc={acc} c={c} />
    </section>
  );
};
